import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  Container,
  Row,
  Col,
  Table,
  CardFooter,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
} from "reactstrap";
import Swal from "sweetalert2";
import CommonHeader from "components/Headers/CommonHeader";
import CustomPagination from "components/Common/CustomPagination";
import { getAllReports, getReportById, replyReport } from "../../api/ApiServices";
import {
  FaFileAlt,
  FaPaperPlane,
  FaEnvelope,
  FaReply,
  FaCheckCircle,
} from "react-icons/fa";

const ReportManagement = () => {
  const [reports, setReports] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [totalReports, setTotalReports] = useState(0);
  const [loading, setLoading] = useState(false);

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  // Reply Form State
  const [replySubject, setReplySubject] = useState("");
  const [replyMessage, setReplyMessage] = useState("");
  const [sendingReply, setSendingReply] = useState(false);

  const limit = 10;

  // Fetch reports for listing
  const fetchReports = async (page = 1, searchTerm = "") => {
    try {
      setLoading(true);
      const data = await getAllReports({ page, limit, search: searchTerm });
      setReports(data.data || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotalReports(data.pagination?.totalReports || (data.data || []).length);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Failed to fetch reports!",
        timer: 2000,
        showConfirmButton: false,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports(currentPage, search);
  }, [currentPage, search]);

  // Open View & Reply Modal
  const handleOpenViewReplyModal = async (reportItem) => {
    try {
      setModalLoading(true);
      setModalOpen(true);
      setReplyMessage("");
      setReplySubject("Update regarding your report - Area Trek Support");

      const id = reportItem.uuid || reportItem._id;
      const data = await getReportById(id);
      setSelectedReport(data);
    } catch (error) {
      // Fallback to local item if detail call fails
      setSelectedReport({
        report: reportItem,
        reporterUser: reportItem.reporterUser || null,
      });
    } finally {
      setModalLoading(false);
    }
  };

  // Submit Email Reply to User
  const handleSendEmailReply = async (e) => {
    e.preventDefault();

    if (!replyMessage.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Reply Message Required",
        text: "Please type a reply message to send to the user.",
      });
      return;
    }

    const reportId =
      selectedReport?.report?.uuid ||
      selectedReport?.uuid ||
      selectedReport?.report?._id ||
      selectedReport?._id;

    const userEmail =
      selectedReport?.reporterUser?.email ||
      selectedReport?.reporterUser?.uc_email;

    if (!userEmail) {
      Swal.fire({
        icon: "error",
        title: "No Email Found",
        text: "Could not find a valid email address for this user.",
      });
      return;
    }

    try {
      setSendingReply(true);

      await replyReport(reportId, {
        email: userEmail,
        subject: replySubject.trim() || "Update on your report - Area Trek",
        replyMessage: replyMessage.trim(),
      });

      Swal.fire({
        icon: "success",
        title: "Reply Sent!",
        text: `Your reply has been sent to ${userEmail} successfully.`,
        timer: 2500,
        showConfirmButton: false,
      });

      setModalOpen(false);
      setReplyMessage("");
      // Refresh list to show updated status
      fetchReports(currentPage, search);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed to Send Email",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Could not deliver email to user. Please verify SMTP settings.",
      });
    } finally {
      setSendingReply(false);
    }
  };

  return (
    <>
      <CommonHeader />
      <Container className="mt--7" fluid>
        <Row>
          <div className="col">
            <Card className="shadow border-0">
              <CardHeader className="d-flex justify-content-between align-items-center bg-white border-0 py-3">
                <div className="d-flex align-items-center">
                  <div
                    className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center mr-3 shadow-sm"
                    style={{ width: "42px", height: "42px" }}
                  >
                    <FaFileAlt size={18} />
                  </div>
                  <div>
                    <h3 className="mb-0 font-weight-bold text-dark">User Reports</h3>
                    <small className="text-muted">
                      Review user complaints and reply directly via email
                    </small>
                  </div>
                </div>
                <Input
                  type="search"
                  placeholder="Search by reason..."
                  style={{ width: "260px" }}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setCurrentPage(1);
                  }}
                />
              </CardHeader>

              {loading ? (
                <div className="text-center p-5">
                  <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader"></i>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table className="align-items-center table-flush" responsive>
                    <thead className="thead-light">
                      <tr>
                        <th style={{ width: "80px" }}>Sr.No</th>
                        <th style={{ minWidth: "180px" }}>Reported By</th>
                        <th style={{ minWidth: "260px", maxWidth: "380px" }}>Report Reason</th>
                        <th style={{ width: "150px" }}>Submitted Date</th>
                        <th style={{ width: "150px" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reports.length > 0 ? (
                        reports.map((report, index) => {
                          const reasonText =
                            report.report || report.r_reason || report.reason || "—";
                          const dateText = report.createdAt || report.r_created_at;
                          const reporter = report.reporterUser;

                          return (
                            <tr key={report.uuid || report._id}>
                              <td>{index + 1 + (currentPage - 1) * limit}</td>
                              <td>
                                {reporter ? (
                                  <div>
                                    <span className="font-weight-bold text-dark d-block">
                                      {reporter.fullName || reporter.uc_full_name || "User"}
                                    </span>
                                    <small className="text-muted">
                                      {reporter.email || reporter.uc_email || "—"}
                                    </small>
                                  </div>
                                ) : (
                                  <span className="text-muted small">Anonymous / Not found</span>
                                )}
                              </td>
                              <td style={{ maxWidth: "380px", whiteSpace: "normal" }}>
                                <div
                                  className="font-weight-bold text-dark"
                                  title={reasonText}
                                  style={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    wordBreak: "break-word",
                                    lineHeight: "1.45",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => handleOpenViewReplyModal(report)}
                                >
                                  {reasonText}
                                </div>
                              </td>
                              <td className="text-muted small">
                                {dateText
                                  ? new Date(dateText).toLocaleDateString()
                                  : "—"}
                              </td>
                              <td>
                                <Button
                                  color="primary"
                                  size="sm"
                                  onClick={() => handleOpenViewReplyModal(report)}
                                  title="View details and reply via email"
                                  className="d-inline-flex align-items-center shadow-sm"
                                >
                                  <FaReply size={12} className="mr-1" />
                                  <span>View & Reply</span>
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="5" className="text-center py-4 text-muted">
                            No reports found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}

              {/* Pagination */}
              {!loading && reports.length > 0 && (
                <CardFooter className="py-4 border-0">
                  <CustomPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={totalReports}
                    itemsPerPage={limit}
                    onPageChange={(page) => setCurrentPage(page)}
                    itemName="reports"
                  />
                </CardFooter>
              )}
            </Card>
          </div>
        </Row>
      </Container>

      {/* =====================================================
          VIEW & EMAIL REPLY MODAL
      ===================================================== */}
      <Modal isOpen={modalOpen} toggle={() => setModalOpen(!modalOpen)} size="lg" centered>
        <ModalHeader toggle={() => setModalOpen(!modalOpen)} className="border-bottom pb-3">
          <div className="d-flex align-items-center">
            <div
              className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center mr-2 shadow-sm"
              style={{ width: "36px", height: "36px" }}
            >
              <FaEnvelope size={16} />
            </div>
            <div>
              <h4 className="font-weight-bold mb-0">Report Details & Email Reply</h4>
              <small className="text-muted">
                Review user report and send response directly to their email inbox
              </small>
            </div>
          </div>
        </ModalHeader>

        {modalLoading ? (
          <ModalBody className="py-5 text-center">
            <i className="fas fa-spinner fa-spin fa-2x gradient-color-loader" />
          </ModalBody>
        ) : selectedReport ? (
          <Form onSubmit={handleSendEmailReply}>
            <ModalBody className="py-4">
              {/* Report Summary Card */}
              <div
                className="p-3 rounded border mb-4"
                style={{ backgroundColor: "#f8f9fe" }}
              >
                <Row className="mb-2">
                  <Col md="7">
                    <small className="text-muted font-weight-bold text-uppercase d-block mb-1">
                      Reported By User
                    </small>
                    <div className="d-flex align-items-center">
                      <div
                        className="rounded-circle bg-info text-white d-flex align-items-center justify-content-center mr-2 font-weight-bold"
                        style={{ width: "32px", height: "32px", fontSize: "14px" }}
                      >
                        {selectedReport.reporterUser?.fullName?.[0] || "U"}
                      </div>
                      <div>
                        <span className="font-weight-bold text-dark d-block" style={{ fontSize: "13px" }}>
                          {selectedReport.reporterUser?.fullName ||
                            selectedReport.reporterUser?.uc_full_name ||
                            "User"}
                        </span>
                        <small className="text-primary font-weight-bold">
                          {selectedReport.reporterUser?.email ||
                            selectedReport.reporterUser?.uc_email ||
                            "—"}
                        </small>
                      </div>
                    </div>
                  </Col>
                  <Col md="5">
                    <small className="text-muted font-weight-bold text-uppercase d-block mb-1">
                      Submitted Date
                    </small>
                    <span className="small text-dark font-weight-bold">
                      {selectedReport.report?.createdAt || selectedReport.report?.r_created_at
                        ? new Date(
                            selectedReport.report?.createdAt ||
                              selectedReport.report?.r_created_at
                          ).toLocaleString()
                        : "—"}
                    </span>
                  </Col>
                </Row>

                <hr className="my-2" />

                <div>
                  <small className="text-muted font-weight-bold text-uppercase d-block mb-1">
                    Report Reason / Complaint:
                  </small>
                  <p
                    className="font-weight-bold text-dark mb-0 p-2 rounded bg-white border"
                    style={{ fontSize: "13px", lineHeight: 1.5, wordBreak: "break-word", whiteSpace: "pre-wrap" }}
                  >
                    "{selectedReport.report?.report ||
                      selectedReport.report?.r_reason ||
                      selectedReport.report?.reason ||
                      "—"}"
                  </p>
                </div>
              </div>

              {/* Previous Admin Reply Display if exists */}
              {selectedReport.report?.adminReply && (
                <div
                  className="p-3 rounded border mb-4"
                  style={{ backgroundColor: "#f0fff4", borderColor: "#c6f6d5" }}
                >
                  <div className="d-flex align-items-center mb-1">
                    <FaCheckCircle className="text-success mr-2" />
                    <span className="font-weight-bold text-success small">
                      Previous Admin Reply Sent:
                    </span>
                    {selectedReport.report?.repliedAt && (
                      <small className="text-muted ml-auto">
                        {new Date(selectedReport.report.repliedAt).toLocaleString()}
                      </small>
                    )}
                  </div>
                  <p className="mb-0 text-dark small" style={{ whiteSpace: "pre-line" }}>
                    {selectedReport.report.adminReply}
                  </p>
                </div>
              )}

              {/* Reply Email Form */}
              <div className="border rounded p-3">
                <h5 className="font-weight-bold text-dark mb-3 d-flex align-items-center">
                  <FaPaperPlane className="text-primary mr-2" size={14} />
                  Send Official Email Reply to User
                </h5>

                <FormGroup>
                  <Label className="font-weight-bold small text-muted">
                    Recipient Email:
                  </Label>
                  <Input
                    type="email"
                    value={
                      selectedReport.reporterUser?.email ||
                      selectedReport.reporterUser?.uc_email ||
                      "No email available"
                    }
                    disabled
                    className="bg-light"
                    style={{ fontSize: "13px" }}
                  />
                </FormGroup>

                <FormGroup>
                  <Label className="font-weight-bold small">Email Subject:</Label>
                  <Input
                    type="text"
                    value={replySubject}
                    onChange={(e) => setReplySubject(e.target.value)}
                    placeholder="e.g. Update on your report - Area Trek Support"
                    style={{ fontSize: "13px" }}
                    required
                  />
                </FormGroup>

                <FormGroup className="mb-0">
                  <Label className="font-weight-bold small">
                    Reply Message: <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="textarea"
                    rows="5"
                    value={replyMessage}
                    onChange={(e) => setReplyMessage(e.target.value)}
                    placeholder="Type your response to the user here... This message will be sent directly to their registered email address."
                    style={{ fontSize: "13px", resize: "vertical" }}
                    required
                  />
                </FormGroup>
              </div>
            </ModalBody>

            <ModalFooter className="border-top pt-3">
              <Button
                color="secondary"
                type="button"
                onClick={() => setModalOpen(false)}
                disabled={sendingReply}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                type="submit"
                disabled={sendingReply || !replyMessage.trim()}
                className="d-inline-flex align-items-center"
              >
                <FaPaperPlane className="mr-1" />
                {sendingReply ? "Sending Email..." : "Send Email Reply"}
              </Button>
            </ModalFooter>
          </Form>
        ) : null}
      </Modal>
    </>
  );
};

export default ReportManagement;
